import React, { useState, useRef, useEffect } from 'react';
import { X, Check, Plus, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * MultiSelect - Composant de sélection multiple avec possibilité d'ajouter des options personnalisées
 * 
 * @param {Array} options - Liste des options disponibles sous forme de strings
 * @param {Array} value - Valeurs actuellement sélectionnées
 * @param {Function} onChange - Callback appelé quand les valeurs changent
 * @param {string} placeholder - Texte du placeholder
 * @param {boolean} allowCustom - Permet l'ajout d'options personnalisées
 * @param {Function} onAddCustom - Callback appelé lors de l'ajout d'une option personnalisée
 */
export const MultiSelect = ({ 
  options = [], 
  value = [], 
  onChange, 
  placeholder = 'Sélectionner...', 
  allowCustom = false,
  onAddCustom,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const containerRef = useRef(null);

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsAddingCustom(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrer les options selon le terme de recherche
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Ajouter ou retirer une option
  const toggleOption = (option) => {
    const newValue = value.includes(option)
      ? value.filter(v => v !== option)
      : [...value, option];
    onChange(newValue);
  };

  // Retirer une option sélectionnée
  const removeOption = (option) => {
    onChange(value.filter(v => v !== option));
  };

  // Ajouter une option personnalisée
  const handleAddCustom = () => {
    if (customValue.trim() && !options.includes(customValue.trim())) {
      if (onAddCustom) {
        onAddCustom(customValue.trim());
      }
      // Ajouter l'option et la sélectionner
      onChange([...value, customValue.trim()]);
      setCustomValue('');
      setIsAddingCustom(false);
      setSearchTerm('');
    }
  };

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {/* Zone de sélection */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer',
          'ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          'transition-colors hover:border-secondary'
        )}
      >
        <div className="flex flex-wrap gap-1.5">
          {value.length > 0 ? (
            value.map((item, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="gap-1 pr-1"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <span className="max-w-[150px] truncate">{item}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(item);
                  }}
                  className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </div>
        <ChevronDown
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full rounded-md border border-border bg-card shadow-lg">
          {/* Recherche */}
          <div className="p-2 border-b border-border">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher..."
              className="h-8"
              autoFocus
            />
          </div>

          {/* Liste des options */}
          <div className="max-h-60 overflow-y-auto p-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  onClick={() => toggleOption(option)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    value.includes(option) && 'bg-accent/50'
                  )}
                >
                  <div
                    className={cn(
                      'h-4 w-4 border-2 rounded flex items-center justify-center transition-colors',
                      value.includes(option)
                        ? 'bg-primary border-primary'
                        : 'border-muted-foreground'
                    )}
                  >
                    {value.includes(option) && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span className="flex-1 truncate">{option}</span>
                </div>
              ))
            ) : searchTerm ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                Aucun résultat trouvé
              </div>
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                Aucune option disponible
              </div>
            )}
          </div>

          {/* Ajouter une option personnalisée */}
          {allowCustom && (
            <div className="p-2 border-t border-border">
              {!isAddingCustom ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsAddingCustom(true)}
                  className="w-full justify-start gap-2 text-secondary hover:text-secondary"
                >
                  <Plus className="h-4 w-4" />
                  Ajouter une option personnalisée
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    placeholder="Nouvelle option..."
                    className="h-8 flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustom();
                      } else if (e.key === 'Escape') {
                        setIsAddingCustom(false);
                        setCustomValue('');
                      }
                    }}
                    autoFocus
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddCustom}
                    disabled={!customValue.trim()}
                    className="h-8 px-3"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsAddingCustom(false);
                      setCustomValue('');
                    }}
                    className="h-8 px-3"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;

